import { BorderRadiusConfig } from '../types';

/**
 * Generate SVG path for a rounded rectangle
 */
export const roundedRectPath = (
  x: number,
  y: number,
  width: number,
  height: number,
  borderRadius: number | BorderRadiusConfig = 0
): string => {
  // Handle negative dimensions
  if (width < 0) {
    x = x + width;
    width = Math.abs(width);
  }
  if (height < 0) {
    y = y + height;
    height = Math.abs(height);
  }

  // Normalize border radius
  let tl: number, tr: number, bl: number, br: number;
  
  if (typeof borderRadius === 'number') {
    tl = tr = bl = br = borderRadius;
  } else {
    tl = borderRadius.topLeft ?? 0;
    tr = borderRadius.topRight ?? 0;
    bl = borderRadius.bottomLeft ?? 0;
    br = borderRadius.bottomRight ?? 0;
  }

  // Clamp radius to half of smallest dimension
  const maxRadius = Math.min(width / 2, height / 2);
  tl = Math.min(tl, maxRadius);
  tr = Math.min(tr, maxRadius);
  bl = Math.min(bl, maxRadius);
  br = Math.min(br, maxRadius);

  // Generate path
  return `
    M ${x + tl} ${y}
    L ${x + width - tr} ${y}
    Q ${x + width} ${y} ${x + width} ${y + tr}
    L ${x + width} ${y + height - br}
    Q ${x + width} ${y + height} ${x + width - br} ${y + height}
    L ${x + bl} ${y + height}
    Q ${x} ${y + height} ${x} ${y + height - bl}
    L ${x} ${y + tl}
    Q ${x} ${y} ${x + tl} ${y}
    Z
  `.replace(/\s+/g, ' ').trim();
};

/**
 * Generate border radius for top of bar only (vertical)
 */
export const topRoundedRadius = (radius: number): BorderRadiusConfig => ({
  topLeft: radius,
  topRight: radius,
  bottomLeft: 0,
  bottomRight: 0,
});

/**
 * Generate border radius for bottom of bar only (vertical)
 */
export const bottomRoundedRadius = (radius: number): BorderRadiusConfig => ({
  topLeft: 0,
  topRight: 0,
  bottomLeft: radius,
  bottomRight: radius,
});

/**
 * Generate border radius for right of bar only (horizontal)
 */
export const rightRoundedRadius = (radius: number): BorderRadiusConfig => ({
  topLeft: 0,
  topRight: radius,
  bottomLeft: 0,
  bottomRight: radius,
});

/**
 * Generate border radius for left of bar only (horizontal)
 */
export const leftRoundedRadius = (radius: number): BorderRadiusConfig => ({
  topLeft: radius,
  topRight: 0,
  bottomLeft: radius,
  bottomRight: 0,
});

/**
 * Get appropriate border radius based on bar position in stack
 */
export const getStackedBorderRadius = (
  radius: number | BorderRadiusConfig,
  isFirst: boolean,
  isLast: boolean,
  orientation: 'vertical' | 'horizontal',
  isPositive: boolean
): BorderRadiusConfig => {
  const r = typeof radius === 'number' ? radius : (radius.topLeft ?? radius.topRight ?? 0);
  
  if (orientation === 'vertical') {
    if (isPositive) {
      // Positive values go up
      return {
        topLeft: isLast ? r : 0,
        topRight: isLast ? r : 0,
        bottomLeft: isFirst ? r : 0,
        bottomRight: isFirst ? r : 0,
      };
    } else {
      // Negative values go down
      return {
        topLeft: isFirst ? r : 0,
        topRight: isFirst ? r : 0,
        bottomLeft: isLast ? r : 0,
        bottomRight: isLast ? r : 0,
      };
    }
  } else {
    if (isPositive) {
      // Positive values go right
      return {
        topLeft: isFirst ? r : 0,
        topRight: isLast ? r : 0,
        bottomLeft: isFirst ? r : 0,
        bottomRight: isLast ? r : 0,
      };
    } else {
      // Negative values go left
      return {
        topLeft: isLast ? r : 0,
        topRight: isFirst ? r : 0,
        bottomLeft: isLast ? r : 0,
        bottomRight: isFirst ? r : 0,
      };
    }
  }
};

/**
 * Generate a unique ID
 */
export const generateId = (prefix: string = 'chart'): string => {
  return `${prefix}-${Math.random().toString(36).substring(2, 9)}`;
};

